import { call, put, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";
import * as actionTypes from "../ActionTypes";

import {
  saveGallerySuccess,
  saveGalleryFailure,
  readGallerySuccess,
  readGalleryFailure,
  deleteGallerySuccess,
  deleteGalleryFailure,
  updateGalleryImageSuccess,
  updateGalleryImageFailure,
  getImageBase64Success,
  getImageBase64Failure
} from "./galleryAction.js";

import {
  saveGalleryImage,
  readGallery,
  deleteGallery,
  updateGalleryImage,
  getImageBase64
} from "./galleryAPI";

function* saveGallerySaga(action) {
  try {
    const { apiPayloadRequest } = action.payload;

    const { churchObjId, ministriesObjId, channelObjId, images } = apiPayloadRequest || {};

    const objectId =
      churchObjId || ministriesObjId || channelObjId;

    const objectType = churchObjId
      ? "church"
      : ministriesObjId
      ? "ministry"
      : channelObjId
      ? "channel"
      : null;

    if (!objectId || !images?.length)
      throw new Error("Invalid gallery data. Missing object ID or images.");

    const imageData = images[0];
        const payload = {
      imageName: imageData.imageName || "Gallery Image",
      mobileImageUrl:imageData?.mobileImageUrl,
      tabImageUrl:imageData?.tabImageUrl,
      imageUrl: imageData.url_link,
      ...(churchObjId && { churchObjId }),
      ...(ministriesObjId && { ministriesObjId }),
      ...(channelObjId && { channelObjId }),
    };


    const response = yield call(saveGalleryImage, payload);

    if (response?.type === 1) {
      yield put(saveGallerySuccess({ message: "Gallery saved successfully." }));
          } else {
      throw new Error(response?.error || `Failed to save ${objectType} gallery.`);
    }

    yield put({
      type: actionTypes.READ_GALLERY_REQUEST,
      payload: {
        apiPayloadRequest: {
          ...(churchObjId && { churchObjId }),
          ...(ministriesObjId && { ministriesObjId }),
          ...(channelObjId && { channelObjId }),
        },
      },
    });

    if (action.callback) action.callback({ type: 1 });
  } catch (error) {
    yield put(saveGalleryFailure(error.message));
    toast.error(error.message);
    if (action.callback) action.callback({ type: 0, error: error.message });
  }
}


function* readGallerySaga(action) {
  try {
    const { apiPayloadRequest } = action.payload;
    const data = yield call(readGallery, apiPayloadRequest);

    yield put(readGallerySuccess(data));
    if (action.callback) action.callback({ type: 1, data });
  } catch (error) {
    yield put(readGalleryFailure(error.message));
    toast.error(error.message);
    if (action.callback) action.callback({ type: 0, error: error.message });
  }
}

function* deleteGallerySaga(action) {
  try {
    const { _id, churchObjId, ministriesObjId, channelObjId } = action.payload;

    // 🧠 Determine which ID exists
    const objectId = churchObjId || ministriesObjId || channelObjId;
    const objectType = churchObjId
      ? "church"
      : ministriesObjId
      ? "ministry"
      : channelObjId
      ? "channel"
      : null;

    if (!_id || !objectId) throw new Error("Invalid delete request. Missing ID or Object ID.");

    // ✅ Call delete API
    const data = yield call(deleteGallery, { _id });

    if (data?.type === 1) {
      yield put(deleteGallerySuccess({ message: "Gallery deleted successfully." }));
      toast.success(`${objectType} gallery deleted successfully!`);

      // ✅ Refresh gallery based on type
      yield put({
        type: actionTypes.READ_GALLERY_REQUEST,
        payload: {
          apiPayloadRequest: {
            ...(churchObjId && { churchObjId }),
            ...(ministriesObjId && { ministriesObjId }),
            ...(channelObjId && { channelObjId }),
          },
        },
      });
    } else {
      throw new Error(data?.error || `Failed to delete ${objectType} gallery.`);
    }
  } catch (error) {
    yield put(deleteGalleryFailure(error.message));
    toast.error(error.message);
  }
}


function* updateGalleryImageSaga(action) {
  try {
    const { apiPayloadRequest } = action.payload;

    // 🧩 Extract all possible IDs
    const { _id, churchObjId, ministriesObjId, channelObjId, imageName, imageUrl } =
      apiPayloadRequest || {};
    // 🧠 Determine which ID exists
    const objectId = churchObjId || ministriesObjId || channelObjId;
    const objectType = churchObjId
      ? "church"
      : ministriesObjId
      ? "ministry"
      : channelObjId
      ? "channel"
      : null;

    if (!_id || !objectId) throw new Error("Invalid update request. Missing ID or Object ID.");

    // ✅ Build dynamic payload
    const payload = {
      _id:apiPayloadRequest?._id,
      imageName:apiPayloadRequest?.imageName,
      imageUrl:apiPayloadRequest?.imageUrl,
      mobileImageUrl:apiPayloadRequest?.mobileImageUrl,
      tabImageUrl:apiPayloadRequest?.tabImageUrl,
      ...(churchObjId && { churchObjId }),
      ...(ministriesObjId && { ministriesObjId }),
      ...(channelObjId && { channelObjId }),
    };


    // ✅ Call API
    const data = yield call(updateGalleryImage, payload);

    if (data?.type === 1) {
      yield put(updateGalleryImageSuccess(data));
      toast.success(`${objectType} gallery image updated successfully!`);

      // ✅ Refresh gallery
      yield put({
        type: actionTypes.READ_GALLERY_REQUEST,
        payload: {
          apiPayloadRequest: {
            ...(churchObjId && { churchObjId }),
            ...(ministriesObjId && { ministriesObjId }),
            ...(channelObjId && { channelObjId }),
          },
        },
      });

      if (action.callback) action.callback({ type: 1, data });
    } else {
      throw new Error(data?.message || `Failed to update ${objectType} gallery image.`);
    }
  } catch (error) {
    yield put(updateGalleryImageFailure(error.message));
    toast.error(error.message);
    if (action.callback) action.callback({ type: 0, error: error.message });
  }
}

function* getImageBase64Saga(action) {
  try {
    const { url } = action.payload;
    const data = yield call(getImageBase64, url);

    yield put(getImageBase64Success(data));
    if (action.callback) action.callback({ type: 1, data });
  } catch (error) {
    yield put(getImageBase64Failure(error.message));
    toast.error(error.message);
    if (action.callback) action.callback({ type: 0, error: error.message });
  }
}


export default function* galleryWatcherSaga() {
  yield takeLatest(actionTypes.SAVE_GALLERY_REQUEST, saveGallerySaga);
  yield takeLatest(actionTypes.READ_GALLERY_REQUEST, readGallerySaga);
  yield takeLatest(actionTypes.DELETE_GALLERY_REQUEST, deleteGallerySaga);
  yield takeLatest(actionTypes.UPDATE_GALLERY_IMAGE_REQUEST, updateGalleryImageSaga);
  yield takeLatest(actionTypes.GET_IMAGE_BASE64_REQUEST, getImageBase64Saga);

}
